/**
 * Sync Engine: Processes the offline queue when connection is restored.
 *
 * - Items that fail are tracked with attempt count + last error
 * - After MAX_SYNC_ATTEMPTS failures the item is moved to a dead-letter
 *   queue so it stops blocking sync
 * - Sync only runs when the user is authenticated (server-side mutations
 *   require auth; running it on the login screen would just fail forever)
 * - Items are filtered by userId so a different user logging in on the
 *   same device won't try to sync someone else's pending changes
 * - Photo uploads are processed alongside data mutations
 * - 401/JWT errors mark the item as needsReAuth instead of counting as
 *   a real failure, so we can prompt the user to re-login instead of
 *   silently giving up
 */
import { supabase } from '@/integrations/supabase/client';
import {
  getSyncQueue,
  removeFromSyncQueue,
  updateQueueItem,
  moveToDeadLetter,
  getPhotoQueue,
  removeFromPhotoQueue,
  updatePhotoQueueItem,
  moveToPhotoDeadLetter,
  setLastSuccessfulSync,
  MAX_SYNC_ATTEMPTS,
} from './offlineStorage';
import type { SyncQueueItem, PhotoQueueItem } from './offlineStorage';

export interface SyncResult {
  synced: number;
  failed: number;
  movedToDead: number;
  pending: number;
  needsReAuth: boolean;
}

function isAuthError(err: string): boolean {
  if (!err) return false;
  const e = err.toLowerCase();
  return e.includes('jwt') || e.includes('expired') || e.includes('invalid token')
      || e.includes('not authenticated') || e.includes('401');
}

// ── Process the standard mutation queue ────────────────────────────────

export async function processSyncQueue(currentUserId?: string): Promise<SyncResult> {
  const allItems = getSyncQueue();
  const photos = getPhotoQueue();
  const totalCount = allItems.length + photos.length;
  if (totalCount === 0) return { synced: 0, failed: 0, movedToDead: 0, pending: 0, needsReAuth: false };

  // Only process items belonging to the current user (or legacy items
  // without userId, for backward compat)
  const queue = currentUserId
    ? allItems.filter(i => !i.userId || i.userId === currentUserId)
    : allItems;
  const photosForUser = currentUserId
    ? photos.filter(i => !i.userId || i.userId === currentUserId)
    : photos;

  const sorted = [...queue].sort((a, b) => a.timestamp - b.timestamp);

  let synced = 0;
  let failed = 0;
  let movedToDead = 0;
  let needsReAuth = false;

  for (const item of sorted) {
    const result = await syncItem(item);
    if (result.success) {
      removeFromSyncQueue(item.id);
      synced++;
      continue;
    }
    const errorMsg = result.error || 'unknown error';
    const authError = isAuthError(errorMsg);

    if (authError) {
      // Don't punish auth errors against the attempt budget — they're transient
      // and require user action (re-login). Mark and surface to caller.
      updateQueueItem(item.id, {
        lastError: errorMsg,
        lastAttemptAt: Date.now(),
        needsReAuth: true,
      });
      needsReAuth = true;
      failed++;
      continue;
    }

    const attempts = (item.attempts || 0) + 1;
    if (attempts >= MAX_SYNC_ATTEMPTS) {
      moveToDeadLetter({ ...item, attempts, lastError: errorMsg, lastAttemptAt: Date.now() });
      movedToDead++;
    } else {
      updateQueueItem(item.id, { attempts, lastError: errorMsg, lastAttemptAt: Date.now() });
      failed++;
    }
  }

  // Process queued photos
  for (const photo of photosForUser) {
    const result = await syncPhoto(photo);
    if (result.success) {
      removeFromPhotoQueue(photo.id);
      synced++;
      continue;
    }
    const errorMsg = result.error || 'unknown error';
    const authError = isAuthError(errorMsg);

    if (authError) {
      updatePhotoQueueItem(photo.id, {
        lastError: errorMsg,
        lastAttemptAt: Date.now(),
        needsReAuth: true,
      });
      needsReAuth = true;
      failed++;
      continue;
    }

    const attempts = (photo.attempts || 0) + 1;
    if (attempts >= MAX_SYNC_ATTEMPTS) {
      moveToPhotoDeadLetter({ ...photo, attempts, lastError: errorMsg, lastAttemptAt: Date.now() });
      movedToDead++;
    } else {
      updatePhotoQueueItem(photo.id, { attempts, lastError: errorMsg, lastAttemptAt: Date.now() });
      failed++;
    }
  }

  if (synced > 0) setLastSuccessfulSync();

  return {
    synced,
    failed,
    movedToDead,
    pending: getSyncQueue().length + getPhotoQueue().length,
    needsReAuth,
  };
}

interface SyncItemResult {
  success: boolean;
  error?: string;
}

async function syncItem(item: SyncQueueItem): Promise<SyncItemResult> {
  const { table, action, data, matchColumns } = item;
  try {
    switch (action) {
      case 'insert': {
        const { error } = await supabase.from(table as any).insert(data as any);
        if (error) return { success: false, error: formatErr(error) };
        return { success: true };
      }
      case 'update': {
        if (!matchColumns?.length) return { success: false, error: 'update without matchColumns' };
        let q = supabase.from(table as any).update(data as any);
        for (const col of matchColumns) q = q.eq(col, (data as any)[col]);
        const { error } = await q;
        if (error) return { success: false, error: formatErr(error) };
        return { success: true };
      }
      case 'upsert': {
        const { error } = await supabase.from(table as any).upsert(data as any);
        if (error) return { success: false, error: formatErr(error) };
        return { success: true };
      }
      case 'delete': {
        if (!matchColumns?.length) return { success: false, error: 'delete without matchColumns' };
        let q = supabase.from(table as any).delete();
        for (const col of matchColumns) q = q.eq(col, (data as any)[col]);
        const { error } = await q;
        if (error) return { success: false, error: formatErr(error) };
        return { success: true };
      }
      default:
        return { success: false, error: `unknown action: ${action}` };
    }
  } catch (e: any) {
    return { success: false, error: e?.message || 'exception' };
  }
}

async function syncPhoto(photo: PhotoQueueItem): Promise<SyncItemResult> {
  try {
    // Convert base64 back to Blob
    const byteString = atob(photo.base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: photo.contentType });

    const path = `${photo.userId}/${photo.photoMonth}/${photo.poseType}.${photo.ext}`;

    // Best-effort: remove any existing pose at this slot (so the upload acts
    // as a true overwrite matching the online flow).
    await supabase.storage.from('progress-photos').remove([path]);

    const { error: uploadError } = await supabase.storage
      .from('progress-photos')
      .upload(path, blob, { upsert: true, contentType: photo.contentType });
    if (uploadError) return { success: false, error: formatErr(uploadError) };

    const { data: signedData, error: signedError } = await supabase.storage
      .from('progress-photos')
      .createSignedUrl(path, 60 * 60 * 24 * 30);
    if (signedError) return { success: false, error: formatErr(signedError) };

    // Replace any DB row for the same slot, then insert the fresh one
    await supabase
      .from('progress_photos')
      .delete()
      .eq('user_id', photo.userId)
      .eq('profile_name', photo.profileName)
      .eq('photo_month', photo.photoMonth)
      .eq('pose_type', photo.poseType);

    const { error: insertError } = await supabase.from('progress_photos').insert({
      user_id: photo.userId,
      profile_name: photo.profileName,
      photo_month: photo.photoMonth,
      pose_type: photo.poseType,
      photo_url: signedData.signedUrl,
      storage_path: path,
    });
    if (insertError) return { success: false, error: formatErr(insertError) };
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e?.message || 'exception' };
  }
}

function formatErr(error: any): string {
  if (!error) return 'unknown';
  if (typeof error === 'string') return error;
  return error.message || error.code || JSON.stringify(error).slice(0, 200);
}

/**
 * Call this once at app startup to listen for reconnection and auto-sync.
 * Sync only runs when:
 *   1. The browser is online, AND
 *   2. The user is authenticated.
 */
export function startAutoSync(
  onSyncComplete?: (result: SyncResult) => void,
  onSyncStart?: () => void
) {
  let currentUserId: string | undefined;
  let inFlight = false;
  let scheduled: ReturnType<typeof setTimeout> | null = null;

  // Coalesce calls: multiple triggers (online event + auth change +
  // getSession resolution) commonly fire within milliseconds of each
  // other. Without coalescing, each one would launch its own sync run
  // and produce a duplicate toast. This ensures only one run actually
  // executes per "burst".
  const tryRun = () => {
    if (scheduled) return; // already scheduled
    scheduled = setTimeout(async () => {
      scheduled = null;
      if (inFlight) return;
      if (!navigator.onLine) return;
      if (!currentUserId) return;
      const totalQueued = getSyncQueue().length + getPhotoQueue().length;
      if (totalQueued === 0) return;
      inFlight = true;
      try {
        onSyncStart?.();
        const result = await processSyncQueue(currentUserId);
        onSyncComplete?.(result);
      } finally {
        inFlight = false;
      }
    }, 600); // small debounce window
  };

  window.addEventListener('online', tryRun);

  const { data: authSubscription } = supabase.auth.onAuthStateChange(
    (_event, session) => {
      currentUserId = session?.user?.id;
      if (currentUserId) tryRun();
    }
  );

  supabase.auth.getSession().then(({ data: { session } }) => {
    currentUserId = session?.user?.id;
    if (currentUserId) tryRun();
  });

  return () => {
    window.removeEventListener('online', tryRun);
    authSubscription.subscription.unsubscribe();
    if (scheduled) clearTimeout(scheduled);
  };
}
